// Список субагентов и фоновых задач — как консоль рисует их под последним
// блоком:
//
//   ⏺ Работают 2 агента
//     ├─ Explore · Поиск по коду · работает 1 мин 5 с · 7 вызовов · 12.3k токенов
//     │  ⎿ Grep
//     └─ general-purpose · Сборка отчёта · ✓ готово за 40 с
//
// Список приходит с сервера в session.tasks (обновляется не чаще раза в
// секунду, есть и в снимке hello — переживает перезагрузку страницы). Задача
// видна, пока жива, в том числе после конца хода координатора; готовая —
// ещё 15 минут «готово». Отвязанные процессы (nohup … &, Codex) — тоже здесь:
// сервер находит их по pid. Секундомер тикает здесь, чтобы не гонять ради
// него статусы.

import { memo, useEffect, useState } from 'react'
import type { TaskInfo } from '../protocol'
import './agents.css'

/** сколько секунд готовая задача остаётся карточкой (как TASK_DONE_TTL сервера) */
const DONE_TTL = 15 * 60

const KIND_RU: Record<string, string> = {
  local_bash: 'фоновая команда',
  local_workflow: 'воркфлоу',
  local_agent: 'агент',
  remote_agent: 'облачный агент',
  process: 'фоновый процесс',
}

const STATE_RU: Record<string, string> = {
  done: '✓ готово',
  failed: '✗ ошибка',
  stopped: '■ остановлено',
  interrupted: '✗ оборвано рестартом',
}

function plural(n: number, one: string, few: string, many: string): string {
  if (n % 10 === 1 && n % 100 !== 11) return one
  if (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14)) return few
  return many
}

function duration(sec: number): string {
  const s = Math.max(0, Math.floor(sec))
  if (s < 60) return `${s} с`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m} мин ${s % 60} с`
  return `${Math.floor(m / 60)} ч ${m % 60} мин`
}

function tokens(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n)
}

function isRunning(t: TaskInfo): boolean {
  return (t.state ?? 'running') === 'running'
}

function headline(running: TaskInfo[], finished: number): string {
  if (running.length === 0) {
    return finished === 1 ? 'Фоновая задача завершена' : 'Фоновые задачи завершены'
  }
  const agents = running.filter((t) => t.kind !== 'local_bash' && t.kind !== 'process').length
  const n = running.length
  return agents === n
    ? `${n === 1 ? 'Работает' : 'Работают'} ${n} ${plural(n, 'агент', 'агента', 'агентов')}`
    : `${n === 1 ? 'Идёт' : 'Идут'} ${n} ${plural(n, 'задача', 'задачи', 'задач')} в фоне`
}

function AgentsBlock({ tasks }: { tasks: TaskInfo[] }) {
  const [now, setNow] = useState(() => Date.now() / 1000)
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now() / 1000), 1000)
    return () => window.clearInterval(id)
  }, [])

  // готовые старше TTL прячем сами: сервер вычистит их при следующем статусе
  const shown = tasks.filter((t) => isRunning(t) || now - (t.finishedAt ?? now) <= DONE_TTL)
  if (shown.length === 0) return null
  // сначала идущие, потом готовые — свежие выше
  const running = shown.filter(isRunning)
  const finished = shown
    .filter((t) => !isRunning(t))
    .sort((a, b) => (b.finishedAt ?? 0) - (a.finishedAt ?? 0))
  const list = [...running, ...finished]

  return (
    <div className={running.length ? 't-agents' : 't-agents is-idle'}>
      <div className="t-agents__head">
        <span className="t-agents__glyph">{running.length ? '⏺' : '○'}</span>
        <span>{headline(running, finished.length)}</span>
      </div>
      {list.map((t, i) => {
        const last = i === list.length - 1
        const live = isRunning(t)
        const end = !live && t.finishedAt ? t.finishedAt : now
        const bits = [
          live
            ? `работает ${duration(now - t.startedAt)}`
            : `${STATE_RU[t.state ?? 'done'] ?? t.state} за ${duration(end - t.startedAt)}`,
          t.toolUses ? `${t.toolUses} ${plural(t.toolUses, 'вызов', 'вызова', 'вызовов')}` : '',
          t.tokens ? `${tokens(t.tokens)} токенов` : '',
        ].filter(Boolean)
        return (
          <div className={`t-agents__item is-${t.state ?? 'running'}`} key={t.id}>
            <div className="t-agents__row">
              <span className="t-agents__tree">{last ? '└─' : '├─'}</span>
              <span className="t-agents__text">
                <b>{KIND_RU[t.kind] ?? t.kind}</b>
                {' · '}{t.label}
                <span className="t-agents__stats">{' · '}{bits.join(' · ')}</span>
              </span>
            </div>
            {live && t.lastTool ? (
              <div className="t-agents__row t-agents__sub">
                <span className="t-agents__tree">{last ? '  ' : '│ '}</span>
                <span className="t-agents__text">⎿ {t.lastTool}</span>
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

export default memo(AgentsBlock)
